import React from 'react'
import { Box, Typography } from '@mui/material'
import Question from 'src/feed/FeedComponents/Question'
import SectionTitle from 'src/feed/FeedComponents/SectionTitle'
import { advancedColors } from '../../theme'
import { ActivityContext } from '../../api/patientConversation'
import SectionEntryRow from './activityEntry/SectionEntryRow'

type Props = { label: string; detail: string | null; context: ActivityContext | null; isCompleted?: boolean }

// What the body adds to a row's header, which already carries the title and the summary.
export const hasContextBody = ({ label, detail, context }: Props): boolean =>
  !!detail || (!!context && (context.sections.length > 0 || label !== context.title))

// Not Standard's `Value`: it would collapse the line breaks in a reflection's answer.
const LabelDetail = ({ label, detail }: { label: string; detail: string | null }) =>
  detail ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Question content={label} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 'bold',
          lineHeight: 1.3,
          color: advancedColors.ink,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {detail}
      </Typography>
    </Box>
  ) : (
    <Typography variant="body2" sx={{ color: advancedColors.ink }}>
      {label}
    </Typography>
  )

const ActivityContextBody = ({ label, detail, context, isCompleted = true }: Props) => {
  if (!context) {
    return <LabelDetail label={label} detail={detail} />
  }

  // The row header already shows the title and summary.
  const showLabel = label !== context.title || detail

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {showLabel && <LabelDetail label={label} detail={detail} />}
      {context.sections.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {context.sections.map((section) => (
            <Box key={section.sectionID}>
              {section.title && (
                <Box sx={{ mb: 0.5 }}>
                  <SectionTitle content={section.title} score={section.score ?? undefined} />
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.entries.map((entry, index) => (
                  <SectionEntryRow
                    key={entry.entryID}
                    entry={entry}
                    format={section.format}
                    programID={context.programID}
                    isCompleted={isCompleted}
                    questionNumber={index}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default ActivityContextBody
